import { useId, type ReactNode } from 'react';

export type RecordMetadataItem = Readonly<{
  label: string;
  value: ReactNode;
}>;

export type RecordListProps = Readonly<{
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}>;

export type RecordListItemProps = Readonly<{
  heading: ReactNode;
  reference: ReactNode;
  context?: ReactNode;
  summary?: ReactNode;
  metadata?: ReadonlyArray<RecordMetadataItem>;
  actions?: ReactNode;
  className?: string;
}>;

function classes(base: string, extra?: string) {
  return extra ? `${base} ${extra}` : base;
}

export function RecordList({ ariaLabel, children, className }: RecordListProps) {
  return <ul className={classes('record-list', className)} aria-label={ariaLabel}>{children}</ul>;
}

export function RecordListItem({
  heading,
  reference,
  context,
  summary,
  metadata = [],
  actions,
  className,
}: RecordListItemProps) {
  const headingId = useId();
  return <li className="record-list__item">
    <article className={classes('record-list__record', className)} aria-labelledby={headingId}>
      <header className="record-list__header">
        <p className="record-list__reference">{reference}</p>
        {context !== undefined && <p className="record-list__context">{context}</p>}
        <h3 className="record-list__heading" id={headingId}>{heading}</h3>
      </header>
      {summary !== undefined && <p className="record-list__summary">{summary}</p>}
      {metadata.length > 0 && <dl className="record-list__metadata">
        {metadata.map((item) => <div className="record-list__metadata-item" key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>)}
      </dl>}
      {actions !== undefined && <footer className="record-list__actions">{actions}</footer>}
    </article>
  </li>;
}
